import {makeAutoObservable, runInAction} from 'mobx';

import apiService from '../transport/apiService';

import type {RootStore} from './rootStore';

export const MIN_PRICE = 0;
export const MAX_PRICE = 1000;

const fixedFilters = [
  // Specials
  {
    type: 'facet-groups',
    id: '62ccff7c25a0f0320612fe4f',
    sell_price_cents: false,
    department: false,
    specials: true,
    name: 'Specials',
    facets: [
      {
        type: 'facets',
        id: '62ccff7c25a0f0320612fe51',
        count: 8,
        name: 'Specials',
        query_value: 'special:drill',
      },
    ],
    relationshipNames: ['facets'],
  },
  // Price range
  {
    type: 'facet-groups',
    id: '62ccff7c25a0f0320612fe50',
    sell_price_cents: true,
    department: false,
    specials: false,
    name: 'Price',
    facets: [
      {
        query_value: 'price_range:1000-2000',
      },
    ],
  },
];

export class FilterStore {
  rootStore: RootStore;

  isLoadingFilterOptions = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    this.reset();
  }

  reset = () => {
    this.selectedSort = null;
  };

  //
  // SORT
  //
  selectedSort = null;
  sortOptions = [];
  topProducts = null;

  setSelectedSort = (item: any) => {
    runInAction(() => {
      this.selectedSort = item;
      this.rootStore.rangeStore.setSelectedSortOption(item);
    });
  };

  loadSortOptions = async () => {
    runInAction(() => {
      this.sortOptions = [];
      this.isLoadingFilterOptions = true;
    });
    try {
      let data: any = await apiService.getSortOptions();
      data = data.filter(
        (item: any) => !['unit_price', 'discount_percent'].includes(item.id),
      );
      runInAction(() => {
        this.topProducts = data.shift();
        this.sortOptions = data;
      });
    } catch (err) {
      console.log(err);
    } finally {
      runInAction(() => {
        this.isLoadingFilterOptions = false;
      });
    }
  };

  //
  // SORT AND FILTER
  //
  // Need to clear both sort and filters
  // AND
  // hit clear rangeStore selection so it doesn't
  // cause multiple reloads of products.
  clearSortAndFilters = () => {
    runInAction(() => {
      this.selectedSort = this.topProducts;
      this.priceSelected = null;
      this.low = MIN_PRICE;
      this.high = MAX_PRICE;
      this.specialsSelected = null;
      this.subFilters = {};
      this.currentFilters = [];
      this.previousFilters = this.currentFilters;
      this.rootStore.rangeStore.clearSelectedSortAndFilter(this.topProducts);
    });
  };

  //
  // FILTER
  //
  currentFilters = [];
  previousFilters = [];
  filterOptions = null;
  specialsSelected = null;
  priceSelected = null;
  low = 0;
  high = 1000;
  currentParentFilter: any = null;
  subFilters = {};

  loadFilterOptions = async () => {
    try {
      let data: any = await apiService.getFilterOptions();
      const parsed = parseFilterResults(data);
      runInAction(() => {
        this.filterOptions = parsed;
      });
    } catch (err) {
      console.log(err);
    }
  };

  setFilters = (filters: any) => (this.currentFilters = filters);
  setCurrentParentFilter = (filter: any) => (this.currentParentFilter = filter);

  setSpecialsFilter = (filter: any) => {
    if (this.specialsSelected) {
      this.specialsSelected = null;
    } else {
      this.specialsSelected = filter;
    }
  };

  setPriceFilter = (low: number, high: number, item: any) => {
    if (low > MIN_PRICE || high < MAX_PRICE) {
      this.priceSelected = {
        ...item,
        query: `price_range:${low * 100}-${high * 100}`,
      };
    } else {
      this.priceSelected = null;
    }
    this.low = low;
    this.high = high;
  };

  // { brands: [{filter1}, filter2}...],
  //   countries: [{filter1}, {filter2}...]...}
  selectSubFilter = (item: any) => {
    // Use the spread operator to create a new object. React won't re-render
    // if we just modify the old object. Support multiple subfilters per category
    const parentName = this.currentParentFilter.name;
    const newFilters = {...this.subFilters};

    // @ts-ignore
    if (newFilters[parentName]?.has(item)) {
      // item already exists in the filter list, remove it.
      // @ts-ignore
      newFilters[parentName].delete(item);
      // @ts-ignore
    } else if (newFilters[parentName]) {
      // parent filter exists, just add the subfilter to it.
      // @ts-ignore
      newFilters[parentName].add(item);
    } else {
      // @ts-ignore
      // parent filter doesn't exist, give it a new set
      newFilters[parentName] = new Set();
      // @ts-ignore
      newFilters[parentName].add(item);
    }

    runInAction(() => {
      this.subFilters = newFilters;
    });
  };

  filtersChanging = () => {
    this.previousFilters = this.currentFilters;
  };

  clearSubFilters = () => {
    const parentName = this.currentParentFilter.name;
    const newFilters = {...this.subFilters};
    // @ts-ignore
    newFilters[parentName].clear();
    this.subFilters = newFilters;
  };

  clearFilters = () => {
    runInAction(() => {
      this.priceSelected = null;
      this.low = MIN_PRICE;
      this.high = MAX_PRICE;
      this.specialsSelected = null;
      this.subFilters = {};
      this.currentFilters = [];
      this.previousFilters = this.currentFilters;
    });
    this.rootStore.rangeStore.clearSelectedFilterOptions();
  };
}

const parseFilterResults = (data: any) => {
  data.unshift(...fixedFilters);
  // remove departments
  const definitions = {
    Specials: {
      type: 'SINGLE',
      order: 1,
    },
    Price: {
      type: 'SLIDER',
      order: 2,
    },
    Brands: {
      type: 'SUB-SCREEN',
      order: 3,
    },
    Countries: {
      type: 'SUB-SCREEN',
      order: 4,
    },
    Regions: {
      type: 'SUB-SCREEN',
      order: 5,
    },
  };
  data = data
    .filter((group: any) => group.name !== 'Departments')
    .map((group: any) => {
      // @ts-ignore
      const definition = definitions[group.name];
      if (definition.type === 'SINGLE' || definition.type === 'SLIDER') {
        return {
          name: group.name,
          query: group.facets[0].query_value,
          type: definition.type,
          order: definition.order,
        };
      } else {
        return {
          name: group.name,
          subs: group.facets.map((facet: any) => {
            return {
              name: facet.name,
              query: facet.query_value,
              type: 'SUB-SCREEN-SINGLE',
            };
          }),
          type: definition.type,
          order: definition.order,
        };
      }
    });
  // Order options
  data = data.sort(
    (a: {order: number}, b: {order: number}) => a.order - b.order,
  );
  return data;
};
